import { useMemo } from 'react';
import useStory from './useStory';
import { Explainer } from './GuideParts';

// GUIDE 3'S MAIN ANIMATION (rebuilt 20 Oct 2026; Saheb likes it: "inverter
// non inverter ones are good - need those"). Two 1.5 ton ACs, both set to
// 24°C, cool the same hot bedroom. The non-inverter switches fully on and
// off; the inverter slows down and holds 24°C. Then the afternoon sun gets
// stronger than the AC, and both stay at full power. It plays by itself in
// view, rests fully drawn, and has Replay. No chart, no units on screen.
//
// The room is a simple picture of Daikin India's own explanation (one wall
// loss, a little heat from inside, an on/off band of 23 to 25°C), not a
// measurement. Units in a real room are on guides 1 and 2.

const CAP = 5.275;                 // 1.5 ton
const MIN = CAP * 0.3;             // the inverter's slowest
const C = 900;                     // how much heat the room holds, per °C
const UA = 0.18;                   // heat in through walls and roof, per °C
const QINT = 0.4;                  // heat from people and lights
const SET = 24;
const ON_AT = 25;
const OFF_AT = 23;
const START = 31;
const DUR = 3 * 3600;              // three hours, in seconds
const HOT_FROM = 0.7;              // share of the story when the sun takes over
const N = 300;                     // samples drawn

function simulate() {
  let nT = START; let nOn = true; let starts = 1;
  let iT = START; let I = 0;
  let nAng = 0; let iAng = 0;
  const s = [];
  const every = DUR / N;
  for (let sec = 0; sec <= DUR; sec += 1) {
    const share = sec / DUR;
    const tout = share < HOT_FROM ? 38 : 38 + Math.min(1, (share - HOT_FROM) / 0.08) * 17;
    nT += (UA * (tout - nT) + QINT - (nOn ? CAP : 0)) / C;
    if (nOn && nT <= OFF_AT) nOn = false;
    else if (!nOn && nT >= ON_AT) { nOn = true; starts += 1; }
    const err = iT - SET;
    let cap = 1.6 * err + I;
    const sat = cap >= CAP || cap <= MIN;
    if (!sat || (cap >= CAP && err < 0) || (cap <= MIN && err > 0)) I += 0.004 * err;
    cap = Math.max(MIN, Math.min(CAP, cap));
    iT += (UA * (tout - iT) + QINT - cap) / C;
    nAng += nOn ? 2.2 : 0;
    iAng += 2.2 * (cap / CAP);
    if (sec % every < 1) s.push({ nT, nOn, starts, iT, iSpeed: cap / CAP, nAng, iAng });
  }
  return s;
}

function Unit({ angle, on, tone }) {
  return (
    <svg viewBox="0 0 96 96" width="88" height="88" aria-hidden="true" className="block">
      <rect x="8" y="6" width="80" height="20" rx="6" fill="#fff" stroke="#cbd5e1" />
      <circle cx="78" cy="12" r="2.6" fill={on ? '#16a34a' : '#d1d5db'} />
      <rect x="16" y="20" width="64" height="2.5" rx="1.25" fill="#cbd5e1" />
      <rect x="18" y="46" width="60" height="44" rx="7" fill="#fff" stroke="#cbd5e1" />
      <circle cx="48" cy="68" r="16" fill="#f9fafb" stroke="#d1d5db" />
      <g transform={`rotate(${angle % 360} 48 68)`} fill={tone}>
        <path d="M48 68c-1-8 1-12 4-13s4 4 1 8z" /><path d="M48 68c8-1 12 1 13 4s-4 4-8 1z" />
        <path d="M48 68c1 8-1 12-4 13s-4-4-1-8z" /><path d="M48 68c-8 1-12-1-13-4s4-4 8-1z" />
      </g>
      <circle cx="48" cy="68" r="2.6" fill="#4b5563" />
    </svg>
  );
}

function Card({ name, temp, status, speed, angle, on, tone, extra }) {
  return (
    <div className="grid content-start justify-items-center gap-1.5 rounded-xl border border-gray-200 p-3 text-center">
      <p className="text-[15px] font-extrabold text-gray-900">{name}</p>
      <Unit angle={angle} on={on} tone={tone} />
      <p className={`text-[28px] font-extrabold leading-none tabular-nums ${temp <= 24.6 ? 'text-green-700' : 'text-gray-900'}`}>{temp.toFixed(1)}°</p>
      <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[13px] font-bold text-gray-800">{status}</span>
      <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <div className={`h-full rounded-full ${tone === '#2563eb' ? 'bg-blue-600' : 'bg-gray-500'}`} style={{ width: `${Math.round(speed * 100)}%` }} />
      </div>
      <p className="text-[12px] text-gray-500">{extra}</p>
    </div>
  );
}

export default function CompressorExplainer({ watch, sources }) {
  const sim = useMemo(() => simulate(), []);
  const story = useStory(24000);
  const k = Math.min(sim.length - 1, Math.round(story.t * (sim.length - 1)));
  const p = sim[k];
  const hot = story.t >= HOT_FROM + 0.04;
  const iStatus = hot || p.iSpeed > 0.97 ? 'Full power' : 'Slowed down';
  return (
    <Explainer title={watch.title} captions={watch.captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <div className="grid grid-cols-2 gap-2.5">
        <Card name="Non-inverter" temp={p.nT} on={p.nOn} angle={p.nAng} tone="#6b7280"
          status={p.nOn ? 'Full power' : 'Off'} speed={p.nOn ? 1 : 0} extra={`Started ${p.starts} ${p.starts === 1 ? 'time' : 'times'}`} />
        <Card name="Inverter" temp={p.iT} on angle={p.iAng} tone="#2563eb"
          status={iStatus} speed={p.iSpeed} extra="Started 1 time" />
      </div>
      <p className="text-center text-[13px] text-gray-500">{hot ? 'Strong afternoon sun' : 'A hot day'} · both set to 24°</p>
    </Explainer>
  );
}
